import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';

export interface SortableColumn<T> {
  id: keyof T;
  label: string;
  align?: 'left' | 'center' | 'right';
  render?: (row: T) => React.ReactNode;
  headerRender?: () => React.ReactNode;
  sortable?: boolean;
}

export interface SortableTableProps<T> {
  data: T[];
  columns: SortableColumn<T>[];
  loading?: boolean;
  emptyMessage?: React.ReactNode;
  stickyHeader?: boolean;
  maxHeight?: number;
  onRowClick?: (row: T) => void;
  className?: string;
}

export type SortOrder = 'asc' | 'desc' | null;

const SortableTable = <T extends Record<string, any>>({
  data,
  columns,
  loading = false,
  emptyMessage = "No data found",
  stickyHeader = true,
  maxHeight = 500,
  onRowClick,
  className = "",
}: SortableTableProps<T>) => {
  const [orderBy, setOrderBy] = useState<keyof T | null>(null);
  const [order, setOrder] = useState<SortOrder>(null);

  // Sorting function with three states: asc -> desc -> no sort
  const handleRequestSort = (property: keyof T) => {
    if (orderBy === property) {
      if (order === 'asc') {
        setOrder('desc');
      } else if (order === 'desc') {
        setOrder(null);
        setOrderBy(null);
      }
    } else {
      setOrderBy(property);
      setOrder('asc');
    }
  };

  // Sort the data
  const sortedData = useMemo(() => {
    if (!orderBy || !order) {
      return data; // Return unsorted data if no sorting is applied
    }

    return [...data].sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];

      // Handle numeric values
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return order === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // Handle string values
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return order === 'asc' ? -1 : 1;
      if (bValue == null) return order === 'asc' ? 1 : -1;

      if (aValue < bValue) {
        return order === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return order === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [data, orderBy, order]);

  return (
    <TableContainer 
      sx={{ maxHeight: maxHeight, overflow: "auto" }}
      className={className}
    >
      <Table stickyHeader={stickyHeader}>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell
                key={String(column.id)}
                align={column.align || "left"}
                sx={{
                  backgroundColor: "rgba(191, 229, 255, 1) !important",
                  position: stickyHeader ? "sticky" : "static",
                  top: 0,
                  zIndex: 1,
                }}
              >
                {column.headerRender ? (
                  column.headerRender()
                ) : column.sortable !== false ? (
                  <TableSortLabel
                    active={orderBy === column.id}
                    direction={orderBy === column.id && order ? order : 'asc'}
                    onClick={() => handleRequestSort(column.id)}
                    hideSortIcon={false}
                    sx={{
                      '& .MuiTableSortLabel-icon': {
                        color: orderBy === column.id ? 'rgba(13, 44, 90, 1) !important' : 'rgba(13, 44, 90, 0.3) !important',
                        opacity: 1,
                      },
                      '&.Mui-active': {
                        color: 'rgba(13, 44, 90, 1) !important',
                      },
                      '&:hover': {
                        color: 'rgba(13, 44, 90, 0.7) !important',
                        '& .MuiTableSortLabel-icon': {
                          color: 'rgba(13, 44, 90, 0.7) !important',
                        },
                      },
                    }}
                  >
                    <Typography variant="body2" className="fw-bold">
                      {column.label}
                    </Typography>
                  </TableSortLabel>
                ) : (
                  <Typography variant="body2" className="fw-bold">
                    {column.label}
                  </Typography>
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                <Typography variant="body2" color="textSecondary">
                  Loading...
                </Typography>
              </TableCell>
            </TableRow>
          ) : data.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length} align="center">
                {typeof emptyMessage === 'string' ? (
                  <Typography variant="body2" color="textSecondary" sx={{ py: 4 }}>
                    {emptyMessage}
                  </Typography>
                ) : (
                  emptyMessage
                )}
              </TableCell>
            </TableRow>
          ) : (
            sortedData.map((row, index) => (
              <TableRow 
                key={row.id || index} 
                hover 
                onClick={() => onRowClick?.(row)}
                sx={{ cursor: onRowClick ? 'pointer' : 'default' }}
              >
                {columns.map((column) => (
                  <TableCell key={String(column.id)} align={column.align || "left"}>
                    {column.render ? column.render(row) : (row as any)[column.id]}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default SortableTable;
